import { useEffect, useState } from 'react'
import { Syringe, ChevronDown, ChevronUp } from 'lucide-react'
import { ApiError } from '../lib/api'
import { useLang, t, fmt } from '../lib/i18n'
import {
  SAMPLE_IMMUNIZATIONS,
  SAMPLE_DOB,
  buildRecommendations,
  vaccineDisplayFor,
  type DoseForecast,
  type ImmunizationView,
  type Recommendation,
} from '../lib/samples/immunizations'

/**
 * ImmunizationsPanel — vaccines given + WHO reference dose-forecast.
 *
 * Backend: the Immunization model + schemas exist (apps/api/app/schemas/
 * immunization.py) but no router/endpoint is deployed yet. This panel is
 * therefore API-READY: it attempts the future contract
 *
 *   GET /api/v1/patients/{id}/immunizations → { items, total }
 *
 * first (matching ImmunizationListResponse), and on any failure renders the
 * bundled contract-exact samples from lib/samples/immunizations.ts with the
 * existing "Sample data" badge (prov.sampleData) — the same honest-state
 * pattern as VitalsPanel (loading skeleton → ready/error, badge on fallback).
 *
 * The forecast engine (buildRecommendations/forecastNextDose) is ported from
 * the dad's prototype (PHRPrototype_5.jsx, VACCINES_SY + forecastNextDose) —
 * WHO reference schedule only, NOT a confirmed Syrian MoH/EPI schedule, so
 * every recommendation view carries the bilingual governance caveat banner
 * required by the prototype's governance rule.
 */

type PanelState =
  | { phase: 'loading' }
  | { phase: 'ready'; items: ImmunizationView[]; sampled: boolean }

/** Placeholder client for the future immunizations endpoint. */
async function listPatientImmunizations(
  patientId: string,
  options: { signal?: AbortSignal } = {},
): Promise<{ items: ImmunizationView[]; total: number }> {
  const API_BASE =
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
    (typeof window !== 'undefined' ? `${window.location.origin}/api/v1` : 'http://127.0.0.1:8000/api/v1')
  const res = await fetch(`${API_BASE}/patients/${patientId}/immunizations`, { signal: options.signal })
  if (!res.ok) {
    let detail: unknown = res.statusText
    try {
      detail = (await res.json()).detail ?? res.statusText
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, detail)
  }
  return (await res.json()) as { items: ImmunizationView[]; total: number }
}

function dateFmt(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso.length > 10 ? iso.slice(0, 10) : iso)
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

/** Status chip classes for a given-dose row (translated label, stable color). */
function statusChip(completion: string | null | undefined): { cls: string; key: string } {
  switch (completion) {
    case 'complete':
      return { cls: 'border-emerald-200 bg-emerald-50 text-emerald-800', key: 'imm.status.complete' }
    case 'partial':
      return { cls: 'border-amber-200 bg-amber-50 text-amber-800', key: 'imm.status.partial' }
    case 'not-done':
      return { cls: 'border-stone-200 bg-stone-100 text-stone-600', key: 'imm.status.notDone' }
    case 'entered-in-error':
      return { cls: 'border-red-200 bg-red-50 text-red-800', key: 'imm.status.error' }
    default:
      return { cls: 'border-stone-200 bg-stone-50 text-stone-700', key: 'imm.status.recorded' }
  }
}

/** Chip classes + label key for a dose-forecast status. */
function forecastChip(forecast: DoseForecast): { cls: string; label: () => string } {
  switch (forecast.status) {
    case 'dueNow':
      return { cls: 'border-emerald-300 bg-emerald-50 text-emerald-800', label: () => t('imm.dueNow') }
    case 'dueOn':
      return {
        cls: 'border-amber-300 bg-amber-50 text-amber-800',
        label: () => `${t('imm.dueOn')} ${dateFmt(forecast.date)}`,
      }
    case 'notYetEligible':
      return {
        cls: 'border-stone-200 bg-stone-100 text-stone-600',
        label: () => `${t('imm.notYetEligible')} ${dateFmt(forecast.date)}`,
      }
    case 'seriesComplete':
      return { cls: 'border-slate-200 bg-slate-50 text-slate-700', label: () => t('imm.seriesComplete') }
  }
}

export function ImmunizationsPanel({ patientId }: { patientId: string }) {
  useLang()
  const [state, setState] = useState<PanelState>({ phase: 'loading' })
  const [recsOpen, setRecsOpen] = useState(true)

  useEffect(() => {
    const controller = new AbortController()
    setState({ phase: 'loading' })
    listPatientImmunizations(patientId, { signal: controller.signal })
      .then((res) => {
        if (controller.signal.aborted) return
        const items = [...(res.items ?? [])].sort(
          (a, b) =>
            new Date(b.date_administered ?? 0).getTime() - new Date(a.date_administered ?? 0).getTime(),
        )
        setState(
          items.length === 0
            ? { phase: 'ready', items: SAMPLE_IMMUNIZATIONS, sampled: true }
            : { phase: 'ready', items, sampled: false },
        )
      })
      .catch(() => {
        if (controller.signal.aborted) return
        // No endpoint yet / unreachable: demo fallback with "Sample data" badge.
        setState({ phase: 'ready', items: SAMPLE_IMMUNIZATIONS, sampled: true })
      })
    return () => controller.abort()
  }, [patientId])

  const sampled = state.phase === 'ready' && state.sampled
  const items = state.phase === 'ready' ? state.items : []
  // WHO-schedule dose forecast — sample stories drive a live-looking demo of
  // all four statuses: dueNow / dueOn / notYetEligible / seriesComplete.
  const recommendations: Recommendation[] =
    state.phase === 'ready' ? buildRecommendations(SAMPLE_DOB, items) : []

  return (
    <section
      className="rounded-xl border border-stone-200 bg-white p-5 shadow-soft"
      aria-label={t('imm.aria')}
      data-testid="immunizations-section"
    >
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Syringe className="h-5 w-5 text-primary-700" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-stone-950">{t('imm.title')}</h2>
        </div>
        {sampled && (
          <span
            className="inline-flex items-center rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800"
            data-testid="sample-data-badge"
          >
            {t('prov.sampleData')}
          </span>
        )}
      </div>

      {state.phase === 'loading' && (
        <div className="space-y-3" role="status" aria-label={t('imm.loading')}>
          <div className="skeleton h-10" />
          <div className="skeleton h-10" />
        </div>
      )}

      {state.phase === 'ready' && items.length === 0 && (
        <p className="px-1 py-6 text-center text-sm text-stone-500">{t('imm.empty')}</p>
      )}

      {state.phase === 'ready' && items.length > 0 && (
        <ul className="divide-y divide-stone-100" data-testid="immunizations-list">
          {items.map((iz) => {
            const display = vaccineDisplayFor(iz)
            const chip = statusChip(iz.completion_status)
            return (
              <li key={iz.id} className="flex items-start gap-3 py-3" data-testid="immunizations-row">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-2" dir="auto">
                    {/* Bilingual master-list name; data itself may be EN-only. */}
                    <span className="text-base font-semibold text-stone-900">
                      <span lang="en">{display.en}</span>
                      <span className="mx-1.5 text-stone-300" aria-hidden="true">
                        ·
                      </span>
                      <span lang="ar" dir="rtl" className="font-medium">
                        {display.ar}
                      </span>
                    </span>
                    {iz.dose_number_in_series != null && (
                      <span className="text-xs font-medium text-stone-500">
                        {fmt(t('imm.doseOf'), { n: iz.dose_number_in_series })}
                      </span>
                    )}
                    {iz.cvx_code && (
                      <span
                        className="inline-flex items-center rounded-md border border-sky-200 bg-sky-50 px-1.5 py-0.5 font-mono text-[10px] text-sky-800"
                        title={fmt(t('imm.cvxTitle'), { code: iz.cvx_code })}
                      >
                        CVX {iz.cvx_code}
                      </span>
                    )}
                    <span
                      className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${chip.cls}`}
                      data-testid={`imm-status-${iz.completion_status ?? 'recorded'}`}
                    >
                      {t(chip.key)}
                    </span>
                  </div>
                  <span className="mt-0.5 block text-xs text-stone-500" dir="auto">
                    {dateFmt(iz.date_administered)}
                    {iz.source === 'patient_self_report' && ` · ${t('imm.selfReported')}`}
                  </span>
                </div>
              </li>
            )
          })}
        </ul>
      )}

      {/* ---------- Recommendations (WHO reference forecast) ---------- */}
      {state.phase === 'ready' && recommendations.length > 0 && (
        <div className="mt-5" data-testid="imm-recommendations">
          <button
            type="button"
            onClick={() => setRecsOpen((prev) => !prev)}
            aria-expanded={recsOpen}
            className="mb-2 flex w-full items-center justify-between rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-sm font-semibold text-stone-800 transition hover:bg-stone-100"
          >
            <span>{t('imm.recommendations')}</span>
            {recsOpen ? (
              <ChevronUp className="h-4 w-4 text-stone-500" aria-hidden="true" />
            ) : (
              <ChevronDown className="h-4 w-4 text-stone-500" aria-hidden="true" />
            )}
          </button>

          {recsOpen && (
            <>
              <p
                className="mb-3 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 text-[11px] leading-snug text-amber-900"
                data-testid="imm-caveat"
              >
                {t('imm.caveat')}
              </p>
              <ul className="divide-y divide-stone-100">
                {recommendations.map(({ group, forecast }) => {
                  const chip = forecastChip(forecast)
                  return (
                    <li key={group.code} className="flex items-start gap-3 py-3" data-testid="imm-recommendation">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-2" dir="auto">
                          <span className="text-sm font-semibold text-stone-900">
                            <span lang="en">{group.en}</span>
                            <span className="mx-1.5 text-stone-300" aria-hidden="true">
                              ·
                            </span>
                            <span lang="ar" dir="rtl" className="font-medium">
                              {group.ar}
                            </span>
                          </span>
                          <span
                            className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${chip.cls}`}
                            data-testid={`imm-forecast-${forecast.status}`}
                          >
                            {chip.label()}
                          </span>
                        </div>
                        {forecast.status === 'seriesComplete' &&
                          forecast.boosterText &&
                          forecast.boosterText !== 'None routinely' && (
                            <span className="mt-0.5 block text-xs text-stone-500">
                              {t('imm.boosterNote')}: {forecast.boosterText}
                            </span>
                          )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            </>
          )}
        </div>
      )}
    </section>
  )
}